import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';
import { color } from '../../../app/theme';
import { ModalType } from '../../../app/types';
import {
  checkAdvanceSetting,
  checkBasicSetting,
  checkData,
  checkTriggerSetting,
  setIsSaved,
} from '../../../features/aiBotSlice';
import { closeModal, openModal } from '../../../features/modalSlice';

const useStyles = makeStyles(
  () => ({
    root: {
      position: 'fixed',
      top: 80,
      left: 0,
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      width: 240,
      height: 'calc(100vh - 80px)',
      backgroundColor: color.$3,
      color: color.$10,
    },
    menu: {
      flex: '1 1 100%',
      width: '100%',
      overflowY: 'auto',
    },
    version: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: 44,
      textAlign: 'center',
      fontSize: 11,
      lineHeight: '11px',
      color: '#989898',
      padding: '16px 0',
    },
    upgrade: {
      width: '100%',
      textAlign: 'center',
    },
    titleBlock: {
      padding: '24px 24px 12px',
      display: 'flex',
      justifyContent: 'left',
      alignItems: 'center',
      borderBottom: '1px solid rgba(239, 239, 239, 0.15)',
      '& > img': {
        marginRight: 6,
      },
    },
    basicSettingTitle: {
      position: 'relative',
      color: color.$10,
      lineHeight: 1,
      fontWeight: 500,
      fontSize: 15,
    },
    navLink: {
      cursor: 'pointer',
      position: 'relative',
      display: 'block',
      height: 48,
      lineHeight: '48px',
      fontSize: 17,
      color: color.$7,
      textDecoration: 'none',
      padding: '0 24px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '&.active': {
        backgroundColor: 'rgba(224, 224, 224, 0.1)',
      },
      '&:hover': {
        backgroundColor: 'rgba(224, 224, 224, 0.1)',
        '& > div': {
          display: 'block',
        },
      },
    },
    'root-error': {
      borderLeft: `4px solid ${color.$17}`,
      paddingLeft: '20px',
    },
  }),
  { name: 'SidebarContainer' },
);

function SidebarContainer({ pathKey }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { orgId, id } = useParams();
  const { t } = useTranslation();
  const { isSaved, data, progress } = useSelector(store => store.aiBot);

  const isBasicSettingVerify = checkBasicSetting(data);
  const isTriggerSettingVerify = checkTriggerSetting(data);
  const isAdvanceSettingVerify = checkAdvanceSetting(data);

  const handleCancelStepLink = e => {
    if (checkData(data, progress)) {
      // TODO: do nothing
    } else {
      e.preventDefault();
      dispatch(setIsSaved(true));
      dispatch(
        openModal({
          modalType: ModalType.ALERT_MODAL,
          modalProps: {
            title: t(`${pathKey}:testing-modal-title`),
            content: t(`${pathKey}:testing-modal-content`),
            onClick: async () => {
              dispatch(closeModal());
            },
            submitText: t('modal.ok-i-know'),
          },
        }),
      );
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.menu}>
        <div className={classes.titleBlock} style={{ marginTop: 8 }}>
          <Typography variant="subtitle2" className={classes.basicSettingTitle}>
            {t(`${pathKey}:knowledgeTitle`)}
          </Typography>
        </div>
        <NavLink
          className={classnames(classes.navLink, {
            [classes['root-error']]: isSaved && !progress?.done,
          })}
          onClick={() => {}}
          to={`/${pathKey}/${orgId}/${id}/knowledge`}
          activeStyle={{
            backgroundColor: 'rgba(224, 224, 224, 0.1)',
          }}
        >
          {t(`${pathKey}:knowledge`)}
        </NavLink>
        <div className={classes.titleBlock} style={{ marginTop: 8 }}>
          <Typography variant="subtitle2" className={classes.basicSettingTitle}>
            {t(`${pathKey}:botSettingsTitle`)}
          </Typography>
        </div>
        <NavLink
          className={classnames(classes.navLink, {
            [classes['root-error']]: isSaved && !isBasicSettingVerify,
          })}
          onClick={() => {}}
          to={`/${pathKey}/${orgId}/${id}/basicSetting`}
          activeStyle={{
            backgroundColor: 'rgba(224, 224, 224, 0.1)',
          }}
        >
          {t(`${pathKey}:basicSetting`)}
        </NavLink>
        <NavLink
          className={classnames(classes.navLink, {
            [classes['root-error']]: isSaved && !isTriggerSettingVerify,
          })}
          onClick={() => {}}
          to={`/${pathKey}/${orgId}/${id}/triggerSetting`}
          activeStyle={{
            backgroundColor: 'rgba(224, 224, 224, 0.1)',
          }}
        >
          {t(`${pathKey}:triggerSetting`)}
        </NavLink>
        <NavLink
          className={classnames(classes.navLink, {
            [classes['root-error']]: isSaved && !isAdvanceSettingVerify,
          })}
          onClick={() => {}}
          to={`/${pathKey}/${orgId}/${id}/advanceSetting`}
          activeStyle={{
            backgroundColor: 'rgba(224, 224, 224, 0.1)',
          }}
        >
          {t(`${pathKey}:advanceSetting`)}
        </NavLink>
        <div className={classes.titleBlock} style={{ marginTop: 8 }}>
          <Typography variant="subtitle2" className={classes.basicSettingTitle}>
            {t(`${pathKey}:botTestingTitle`)}
          </Typography>
        </div>
        <NavLink
          className={classnames(classes.navLink)}
          onClick={handleCancelStepLink}
          to={`/${pathKey}/${orgId}/${id}/testing`}
          activeStyle={{
            backgroundColor: 'rgba(224, 224, 224, 0.1)',
          }}
        >
          {t(`${pathKey}:testing`)}
        </NavLink>
      </div>
      <div className={classes.version}>
        {t('sidebar.version', { icon: '\u26A1' })}
      </div>
    </div>
  );
}

export default SidebarContainer;
